import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  selectIsOrderAvailabilityLoading,
  selectWithinDeliveryDistance,
  selectOrderAvailabilityError,
  selectAvailableDays,
} from 'redux/data/orderavailability/orderavailability.selectors';
import { useFieldNames } from '../../../components/new-order/useFieldNames.hooks';
import { useTranslation } from 'react-i18next';
import { useGetOrderType } from './useGetOrderType';
import { useFormContext } from 'react-hook-form';
import { getDeliveryDistanceErrorMessage } from 'utils';

export const useNewOrderForm = (store, { isLocationsPage = false } = {}) => {
  const storeId = store.storeId;
  const { t } = useTranslation();
  const { fieldNames } = useFieldNames();
  const { watch, control } = useFormContext();
  const orderType = useGetOrderType();
  const address = watch(fieldNames.address);
  const isOrderAvailabilityLoading = useSelector(
    selectIsOrderAvailabilityLoading,
  );
  const withinDeliveryDistance = useSelector(state =>
    selectWithinDeliveryDistance(state, storeId),
  );
  const reduxOrderAvailabilityError = useSelector(selectOrderAvailabilityError);
  const availableDays = useSelector(state =>
    selectAvailableDays(state, storeId, orderType),
  );
  const availablePickupDays = useSelector(state =>
    selectAvailableDays(state, storeId, 'PICKUP'),
  );
  const isPickupAvailable = availablePickupDays.length > 0;

  const deliveryDistanceMessage = getDeliveryDistanceErrorMessage(store);

  const orderAvailabilityError = useMemo(() => {
    const orderingUnavailableMessage = t('errors.orderingUnavailable');

    if (orderType === 'DELIVERY') {
      if (
        withinDeliveryDistance === false &&
        !!address &&
        address !== 'new' &&
        address !== 'default'
      )
        return deliveryDistanceMessage;

      if (!!reduxOrderAvailabilityError) return reduxOrderAvailabilityError;
    }

    if (orderType === 'PICKUP' && !!reduxOrderAvailabilityError) {
      if (reduxOrderAvailabilityError === deliveryDistanceMessage) return null;

      return reduxOrderAvailabilityError;
    }

    if (availableDays?.length < 1) {
      if (isLocationsPage) {
        if (orderType === 'PICKUP')
          return t('errors.pickupOrderingUnavailable');
        if (orderType === 'DELIVERY' && isPickupAvailable)
          return t('errors.deliveryOrderingUnavailable');
      }
      return orderingUnavailableMessage;
    }
  }, [
    address,
    availableDays?.length,
    deliveryDistanceMessage,
    isLocationsPage,
    isPickupAvailable,
    orderType,
    reduxOrderAvailabilityError,
    t,
    withinDeliveryDistance,
  ]);

  const showSwitchToPickup =
    isLocationsPage &&
    orderType === 'DELIVERY' &&
    availableDays?.length < 1 &&
    !!isPickupAvailable;

  return {
    fieldNames,
    orderType,
    control,
    isOrderAvailabilityLoading,
    address,
    orderAvailabilityError,
    showSwitchToPickup,
    availableDays,
  };
};
